import { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, Cake, Briefcase, Trophy, Check, Send } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import MetaTags from '@/components/MetaTags';
import { toast } from 'sonner';
import { trackGroupInquiry } from '@/utils/analytics';
import ConfirmationModal from '@/components/ConfirmationModal';

const Eyebrow = ({ children, light }) => (
  <span className="flex items-center gap-3 font-mono uppercase text-xs tracking-[3px]" style={{ color: light ? 'rgba(255,255,255,0.7)' : 'rgba(8,0,16,0.6)' }}>
    <span className="inline-block w-[20px] h-[2px] bg-[#FF2D78]" />
    {children}
  </span>
);

const GroupsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    event_type: '',
    group_size: '',
    city: '',
    preferred_date: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const packages = [
    {
      icon: Cake,
      title: 'Birthday Bash',
      desc: 'Celebrate your birthday in style with a private crawl experience.',
      features: ['Custom route', 'VIP wristbands', 'Birthday shots', 'Reserved areas'],
      accent: '#FF2D78',
      badge: 'Most Popular',
    },
    {
      icon: Users,
      title: 'Bachelorette / Bachelor',
      desc: 'The ultimate last night of freedom. We handle everything.',
      features: ['Party bus option', 'Matching swag', 'Club access', 'Dedicated host'],
      accent: '#00F5FF',
      badge: null,
    },
    {
      icon: Briefcase,
      title: 'Corporate Events',
      desc: 'Team building that doesn\'t suck. Get your crew out of the office.',
      features: ['Custom branding', 'Team activities', 'Open bar options', 'Private venues'],
      accent: '#B8FF2E',
      badge: 'Best Value',
    },
    {
      icon: Trophy,
      title: 'Sports & Alumni',
      desc: 'Game day, reunion, or just because. Rally your squad.',
      features: ['Watch party setup', 'Themed crawls', 'Group discounts', 'Tailgate vibes'],
      accent: '#FF2D78',
      badge: null,
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.event_type || !formData.group_size || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        group_size: parseInt(formData.group_size),
      });
      toast.success('Inquiry submitted!');
      trackGroupInquiry(formData.city, parseInt(formData.group_size), formData.event_type);
      setFormData({
        name: '',
        email: '',
        event_type: '',
        group_size: '',
        city: '',
        preferred_date: '',
        message: '',
      });
      setShowConfirm(true);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen pt-24" data-testid="groups-page">
      <MetaTags
        title="Group Bookings — Private Crawls & Events"
        description="Planning something big? Private bar crawls for birthdays, bachelorettes, corporate events, and more. Let us handle the nightlife."
        path="/groups"
      />
      {/* Hero — Hot Pink */}
      <section className="relative py-20 pb-32 md:pb-20 px-4 overflow-hidden hero-diagonal-clip" style={{ backgroundColor: '#FF2D78' }} data-testid="groups-hero">
        {/* Mobile-only photo bg + tint */}
        <div className="absolute inset-0 md:hidden">
          <img src="https://customer-assets.emergentagent.com/job_50c259f3-df0a-4de3-8458-52ce4eb888c4/artifacts/n8wsd0lf_happy-men-and-women-enjoying-music-at-concert-in-summer-MASF12045.jpg" alt="" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(255,45,120,0.65)' }} />
        </div>
        {/* Mobile-only dark text scrim */}
        <div className="absolute inset-0 md:hidden pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(8,0,16,0.45) 0%, rgba(8,0,16,0.2) 50%, transparent 80%)' }} />
        <div className="max-w-[1400px] mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="flex items-center gap-3 font-mono uppercase text-xs tracking-[3px] text-white/80 md:text-[rgba(8,0,16,0.6)]">
              <span className="inline-block w-[20px] h-[2px] bg-white md:bg-[#FF2D78]" />
              Private Events
            </span>
            <h1 className="font-display text-[48px] md:text-[96px] uppercase mt-2 leading-none font-bold text-white md:text-[#080010]">
              Group Bookings
            </h1>
            <p className="font-body text-lg mt-4 max-w-xl text-white/90 md:text-[rgba(8,0,16,0.7)]">
              Planning something big? Let us handle the nightlife. Private crawls for birthdays, 
              bachelorettes, corporate events, and more.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="hidden md:block" style={{ height: '5px', backgroundColor: '#080010' }} />

      {/* Packages — Warm off-white */}
      <section className="py-32 px-4" style={{ backgroundColor: '#F5F0EE' }} data-testid="packages-section">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {packages.map((pkg, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 rounded-[16px] relative transition-transform duration-200 ease-out hover:-translate-y-1"
                style={{ borderTop: `5px solid ${pkg.accent}` }}
                data-testid={`package-card-${index}`}
              >
                {/* Badge */}
                {pkg.badge && (
                  <span
                    className="absolute top-4 right-4 font-subheading text-[10px] uppercase tracking-[1px] font-bold px-3 py-1 rounded-full"
                    style={{ backgroundColor: '#B8FF2E', color: '#080010' }}
                    data-testid={`package-badge-${index}`}
                  >
                    {pkg.badge}
                  </span>
                )}
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-lg mb-4"
                  style={{ backgroundColor: `${pkg.accent}15` }}
                >
                  <pkg.icon size={24} style={{ color: pkg.accent }} />
                </div>
                <h3 className="font-display text-[34px] uppercase mb-2 font-bold" style={{ color: '#080010' }}>{pkg.title}</h3>
                <p className="font-body text-[#555] text-sm mb-6">{pkg.desc}</p>
                <ul className="space-y-2">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 font-body text-sm" style={{ color: 'rgba(8,0,16,0.8)' }}>
                      <Check size={16} style={{ color: pkg.accent }} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ height: '5px', backgroundColor: '#080010' }} />

      {/* Inquiry Form — Hot Pink */}
      <section className="py-32 px-4" style={{ backgroundColor: '#FF2D78' }} data-testid="inquiry-form-section">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <Eyebrow light>Get Started</Eyebrow>
            <h2 className="font-display text-[40px] md:text-[58px] lg:text-[67px] text-white uppercase mt-2 leading-none">
              Request a Quote
            </h2>
            <p className="font-body mt-4" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </motion.div>

          {/* Trust line */}
          <p className="font-subheading text-[12px] uppercase tracking-[2px] font-bold mb-4" style={{ color: '#B8FF2E' }}>
            We respond within 24 hours
          </p>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="space-y-6 bg-white rounded-[16px] p-8"
            style={{ borderLeft: '4px solid #B8FF2E' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Full Name *</label>
                <input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="inquiry-name-input"
                  className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] rounded"
                  style={{ color: '#080010' }}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="inquiry-email-input"
                  className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] rounded"
                  style={{ color: '#080010' }}
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Event Type *</label>
                <select
                  value={formData.event_type}
                  onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                  data-testid="inquiry-event-type"
                  className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] appearance-none cursor-pointer rounded"
                  style={{ color: '#080010' }}
                >
                  <option value="">Select event type</option>
                  <option value="birthday">Birthday</option>
                  <option value="bachelorette">Bachelorette</option>
                  <option value="bachelor">Bachelor</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="sports">Sports / Alumni</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Group Size *</label>
                <input
                  type="number"
                  value={formData.group_size}
                  onChange={(e) => setFormData({ ...formData, group_size: e.target.value })}
                  data-testid="inquiry-group-size"
                  className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] rounded"
                  style={{ color: '#080010' }}
                  placeholder="Number of people"
                  min="1"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>City *</label>
                <input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  data-testid="inquiry-city-input"
                  className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] rounded"
                  style={{ color: '#080010' }}
                  placeholder="Preferred city"
                />
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Preferred Date</label>
                <input
                  type="date"
                  value={formData.preferred_date}
                  onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                  data-testid="inquiry-date-input"
                  className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] rounded"
                  style={{ color: '#080010' }}
                />
              </div>
            </div>

            <div>
              <label className="font-mono text-[11px] uppercase tracking-wide" style={{ color: '#080010' }}>Additional Details</label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                data-testid="inquiry-message-input"
                className="w-full mt-1 px-4 py-3 bg-white border border-gray-200 font-body focus:outline-none focus:border-b-2 focus:border-b-[#FF2D78] min-h-[120px] resize-none rounded"
                style={{ color: '#080010' }}
                placeholder="Tell us more about your event..."
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="inquiry-submit-btn"
              className="w-full py-4 flex items-center justify-center gap-2 font-display text-lg uppercase tracking-wider rounded-full transition-all duration-150 hover:scale-[1.02]"
              style={{ backgroundColor: '#080010', color: '#FFFFFF' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1a1a1a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#080010'}
            >
              {loading ? 'SUBMITTING...' : 'SUBMIT INQUIRY'} <Send size={18} />
            </button>
          </motion.form>
        </div>
      </section>

      <ConfirmationModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        headline="Say Less. We'll Be in Touch."
        subtext="Someone from the CBC crew will hit you back within 24 hours. Keep an eye on your inbox."
      />
    </div>
  );
};

export default GroupsPage;
